import React, { useState, useEffect } from 'react';
import { reportAPI } from '../../services/api';
import toast from 'react-hot-toast';

const ReportConfig = () => {
  const [activeTab, setActiveTab] = useState('school');
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  
  // School Configuration – matches backend SchoolConfig model
  const [schoolConfig, setSchoolConfig] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    motto: '',
    allow_student_report_print: '', // boolean
    reg_code: '',                // read‑only, for display only
    logo_base64: '',              // full data URL (e.g. "data:image/png;base64,...")
    password_reset_code: '',      // staff password reset code
    lead_password_reset_code: ''  // admin password reset code
  });
  
  // Template Configuration
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateFile, setTemplateFile] = useState(null);
  const [makeDefault, setMakeDefault] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchSchoolConfig();
    fetchTemplates();
  }, []);

  const fetchSchoolConfig = async () => {
    try {
      const response = await reportAPI.getSchoolConfig();
      if (response.data) {
        setSchoolConfig(response.data);
      }
    } catch (error) {
      console.error('Error fetching school config:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await reportAPI.getTemplates();
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSchoolConfigSave = async () => {
    try {
      setLoading(true);
      // Send all updatable fields (including full logo data URL)
      const payload = {
        name: schoolConfig.name,
        email: schoolConfig.email,
        phone: schoolConfig.phone,
        address: schoolConfig.address,
        website: schoolConfig.website,
        motto: schoolConfig.motto,
        logo_base64: schoolConfig.logo_base64,
        allow_student_report_print: schoolConfig.allow_student_report_print,
        password_reset_code: schoolConfig.password_reset_code,
        lead_password_reset_code: schoolConfig.lead_password_reset_code
      };
      await reportAPI.updateSchoolConfig(payload);
      toast.success('School configuration saved successfully!');
    } catch (error) {
      toast.error('Failed to save school configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateUpload = async () => {
    if (!templateName || !templateFile) {
      toast.error('Template name and file are required');
      return;
    }

    const formData = new FormData();
    formData.append('name', templateName);
    formData.append('description', templateDescription);
    formData.append('file', templateFile);
    formData.append('is_default', makeDefault.toString());

    try {
      setLoading(true);
      await reportAPI.uploadTemplate(formData);
      toast.success('Template uploaded successfully!');
      // Reset form
      setTemplateName('');
      setTemplateDescription('');
      setTemplateFile(null);
      setMakeDefault(false);
      // Refresh templates
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to upload template');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (templateId) => {
    try {
      await reportAPI.setDefaultTemplate(templateId);
      toast.success('Template set as default');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to set default template');
    }
  };

  const handleDeleteTemplate = async (templateId, isDefault) => {
    if (isDefault) {
      toast.error('Cannot delete default template');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await reportAPI.deleteTemplate(templateId);
        toast.success('Template deleted');
        fetchTemplates();
      } catch (error) {
        toast.error('Failed to delete template');
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/html') {
      setTemplateFile(file);
    } else {
      toast.error('Please select an HTML file');
    }
  };

  // Handle logo upload – keep full data URL
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 3MB)
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Logo must not exceed 3MB');
      return;
    }

    // Validate file type
    if (!file.type.match('image.*')) {
      toast.error('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      // Store the full data URL (including prefix)
      setSchoolConfig({ ...schoolConfig, logo_base64: event.target.result });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-xl p-6 text-gray-700">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Report Configuration</h1>
            <p className="opacity-90 mt-1">Customize report templates and school information</p>
          </div>
          <div className="hidden md:block">
            <i className="fas fa-file-pdf text-5xl opacity-20"></i>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b">
          <nav className="flex space-x-4 px-6 pt-4">
            <button
              className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${activeTab === 'school' ? 'bg-primary-100 text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('school')}
            >
              <i className="fas fa-school mr-2"></i>
              School Configuration
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${activeTab === 'templates' ? 'bg-primary-100 text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('templates')}
            >
              <i className="fas fa-file-pdf mr-2"></i>
              Template Management
            </button>
          </nav>
        </div>

        {/* School Configuration Tab */}
        {activeTab === 'school' && (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* School Info Form */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">School Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    School Name *
                  </label>
                  <input
                    type="text"
                    value={schoolConfig.name}
                    onChange={(e) => setSchoolConfig({...schoolConfig, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter school name"
                  />
                </div>

                {/* Logo upload field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    School Logo (max 3MB)
                  </label>
                  <div className="flex gap-3 items-start">
                    <div className="flex-1">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                        <div className="text-center">
                          <i className="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"></i>
                          <p className="text-sm text-gray-600 mb-2">
                            {schoolConfig.logo_base64 ? 'Logo selected' : 'Click to upload logo'}
                          </p>
                          <label className="cursor-pointer bg-primary-100 text-primary-600 py-2 px-4 rounded-md hover:bg-primary-200 transition-colors text-sm font-medium">
                            <i className="fas fa-upload mr-2"></i>
                            Choose File
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleLogoUpload}
                              className="hidden"
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="w-20 h-20 border rounded-md overflow-hidden flex-shrink-0">
                      {schoolConfig.logo_base64 ? (
                        <img 
                          src={schoolConfig.logo_base64}
                          alt="Logo preview" 
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <i className="fas fa-image text-gray-400"></i>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG or SVG up to 3MB
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address *
                  </label>
                  <textarea
                    value={schoolConfig.address}
                    onChange={(e) => setSchoolConfig({...schoolConfig, address: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows="2"
                    placeholder="Enter school address"
                  />
                </div>

                {/* Read‑only reg_code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    School Registration Code (read‑only)
                  </label>
                  <input
                    type="text"
                    value={schoolConfig.reg_code || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Staff Password Reset Code
                  </label>
                  <input
                    type="text"
                    value={schoolConfig.password_reset_code || ''}
                    onChange={(e) => setSchoolConfig({...schoolConfig, password_reset_code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter staff password reset code"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Password Reset Code
                  </label>
                  <input
                    type="text"
                    value={schoolConfig.lead_password_reset_code || ''}
                    onChange={(e) => setSchoolConfig({...schoolConfig, lead_password_reset_code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter admin password reset code"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone *
                    </label>
                    <input
                      type="text"
                      value={schoolConfig.phone}
                      onChange={(e) => setSchoolConfig({...schoolConfig, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="+234 123 456 7890"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={schoolConfig.email}
                      onChange={(e) => setSchoolConfig({...schoolConfig, email: e.target.value})}
                      // className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="info@school.edu"
                                          readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <input
                      type="url"
                      value={schoolConfig.website}
                      onChange={(e) => setSchoolConfig({...schoolConfig, website: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="www.school.edu"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Motto
                    </label>
                    <input
                      type="text"
                      value={schoolConfig.motto}
                      onChange={(e) => setSchoolConfig({...schoolConfig, motto: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Knowledge is Power"
                    />
                  </div>
                </div>

                {/*  */}
                <div className="flex items-center justify-between">
  <div>
    <p className="font-medium text-gray-700">Allow Students to Print Results</p>
    <p className="text-xs text-gray-500">
      Students can download their result PDF from their dashboard.
      <span className="text-yellow-600 block mt-1">⚠️ Warning: Each print will consume  credit using the current rate from the school wallet.</span>
    </p>
  </div>
  <label className="relative inline-flex items-center cursor-pointer">
    <input
      type="checkbox"
      name="allow_student_report_print"
      checked={schoolConfig.allow_student_report_print}
      onChange={(e) => setSchoolConfig({...schoolConfig, allow_student_report_print: e.target.checked})}
      className="sr-only peer"
    />
    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
  </label>
</div>

                <button
                  onClick={handleSchoolConfigSave}
                  disabled={loading}
                  className="w-full bg-primary-600 text-gray-700 py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
                >
                  <i className="fas fa-save mr-2"></i>
                  {loading ? 'Saving...' : 'Save School Configuration'}
                </button>
              </div>

              {/* Preview Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Preview</h3>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
                  <div className="text-center space-y-4">
                    <div className="flex justify-center">
                      <div className="w-24 h-24 border rounded-lg overflow-hidden bg-white p-2">
                        {schoolConfig.logo_base64 ? (
                          <img 
                            src={schoolConfig.logo_base64} 
                            alt="School logo" 
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <i className="fas fa-school text-gray-400 text-2xl"></i>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-xl font-bold text-gray-800">
                        {schoolConfig.name || 'School Name'}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {schoolConfig.address || 'School Address'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {schoolConfig.phone ? `Tel: ${schoolConfig.phone}` : 'Phone number'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {schoolConfig.email || 'Email address'}
                      </p>
                      {schoolConfig.website && (
                        <p className="text-sm text-blue-600 mt-1">
                          {schoolConfig.website}
                        </p>
                      )}
                    </div>
                    
                    {schoolConfig.motto && (
                      <div className="italic text-gray-500 border-t pt-4">
                        "{schoolConfig.motto}"
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Variable Reference */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">
                    <i className="fas fa-file-pdf mr-2"></i>
                    Template Variables Reference
                  </h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li><code className="bg-blue-100 px-1 rounded">{"{{ school.name }}"}</code> - School name</li>
                    <li><code className="bg-blue-100 px-1 rounded">{"{{ school.logo }}"}</code> - School logo (full data URL)</li>
                    <li><code className="bg-blue-100 px-1 rounded">{"{{ school.address }}"}</code> - School address</li>
                    <li><code className="bg-blue-100 px-1 rounded">{"{{ school.phone }}"}</code> - School phone</li>
                    <li><code className="bg-blue-100 px-1 rounded">{"{{ school.email }}"}</code> - School email</li>
                    <li><code className="bg-blue-100 px-1 rounded">{"{{ school.motto }}"}</code> - School motto</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Template Management Tab */}
        {activeTab === 'templates' && (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Upload New Template */}
              <div className="lg:col-span-1 space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Upload New Template</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Report Card 2024"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows="3"
                    placeholder="Brief description of this template..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    HTML File *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <div className="text-center">
                      <i className="fas fa-upload text-gray-400 text-2xl mb-2"></i>
                      <p className="text-sm text-gray-600 mb-2">
                        {templateFile ? templateFile.name : 'Choose HTML file'}
                      </p>
                      <label className="cursor-pointer bg-primary-100 text-primary-600 py-2 px-4 rounded-md hover:bg-primary-200 transition-colors text-sm font-medium">
                        <i className="fas fa-upload mr-2"></i>
                        Browse Files
                        <input
                          type="file"
                          accept=".html"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Upload an HTML file with Jinja2 template syntax
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="makeDefault"
                    checked={makeDefault}
                    onChange={(e) => setMakeDefault(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="makeDefault" className="ml-2 text-sm text-gray-700">
                    Set as default template
                  </label>
                </div>

                <button
                  onClick={handleTemplateUpload}
                  disabled={loading || !templateName || !templateFile}
                  className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
                >
                  <i className="fas fa-upload mr-2"></i>
                  {loading ? 'Uploading...' : 'Upload Template'}
                </button>

                {/* Template Variables Help */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2 text-sm">
                    Available Template Variables
                  </h4>
                  <div className="space-y-2">
                    <details className="text-sm">
                      <summary className="cursor-pointer text-blue-600 font-medium">
                        Student Variables
                      </summary>
                      <ul className="mt-1 ml-4 space-y-1 text-xs text-gray-600">
                        <li><code>{"{{ student.name }}"}</code> - Full name</li>
                        <li><code>{"{{ student.reg_no }}"}</code> - Registration number</li>
                        <li><code>{"{{ student.passport_photo }}"}</code> - Photo URL</li>
                        <li><code>{"{{ student.gender }}"}</code> - Gender</li>
                      </ul>
                    </details>
                    <details className="text-sm">
                      <summary className="cursor-pointer text-blue-600 font-medium">
                        Subject Variables
                      </summary>
                      <ul className="mt-1 ml-4 space-y-1 text-xs text-gray-600">
                        <li><code>{ '% for subject in subjects %'}</code> - Loop subjects</li>
                        <li><code>{"{{ subject.name }}"}</code> - Subject name</li>
                        <li><code>{"{{ subject.code }}"}</code> - Subject code</li>
                        <li><code>{"{{ subject.total }}"}</code> - Total score</li>
                      </ul>
                    </details>
                  </div>
                </div>
              </div>

              {/* Template List */}
              <div className="lg:col-span-2">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Available Templates ({templates.length})
                </h3>
                
                {templates.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                    <i className="fas fa-file-pdf text-gray-400 text-3xl mb-2"></i>
                    <p className="text-gray-500">No templates uploaded yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {templates.map((template) => (
                      <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-gray-800">
                                {template.name}
                                {template.is_default && (
                                  <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                    <i className="fas fa-star mr-1 text-xs"></i>
                                    Default
                                  </span>
                                )}
                              </h4>
                            </div>
                            {template.description && (
                              <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                              <span>ID: {template.id}</span>
                              <span>Created: {new Date(template.created_at).toLocaleDateString()}</span>
                              {template.file_path && <span className="text-green-600">File exists</span>}
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            {!template.is_default && (
                              <>
                                <button
                                  onClick={() => handleSetDefault(template.id)}
                                  className="p-2 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                                  title="Set as Default"
                                >
                                  <i className="fas fa-star"></i>
                                </button>
                                <button
                                  onClick={() => handleDeleteTemplate(template.id, template.is_default)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Delete"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">Template Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div 
                className="border rounded-lg p-4 bg-gray-50"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
            <div className="p-4 border-t">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportConfig;